import { format, isSameYear } from 'date-fns';

import { cn } from '@/lib/shadcn/utils';
import type {
  CandidatePathParams,
  CandidateTouchpointApi,
} from '@/modules/candidate-detail/queries/types';
import { useCandidateTouchpointsSuspenseQuery } from '@/modules/candidate-detail/queries/useCandidateTouchpointsQuery';
import { parseDateParam } from '@/utils/dateUtils';

interface ActivityEvent {
  /** What happened, past tense, e.g. "Introduced to you". */
  what: string;
  occurredAt: Date;
  /** Calendar day key, so the same day in different years never merges. */
  dayKey: string;
}

interface CandidateActivityProps {
  firstName: string;
  params: CandidatePathParams;
}

const humanize = (value: string) =>
  value
    .replace(/^Candidate/, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (character) => character.toUpperCase());

/**
 * Touchpoints are status transitions with no actor: a recruiter's manual
 * "Mark as contacted" and Finlay's automated outreach produce the same
 * record. Descriptions therefore stay actor-neutral. The prototype's
 * "purple = Finlay acted" marker returns once touchpoints carry an actor.
 * Wording follows the status semantics in candidateStatusLabels
 * (hiring-manager/utils/candidateDisplay): ContactStarted is not yet
 * delivered, and the assessment is a milestone of its own.
 */
function toEvent(
  touchpoint: CandidateTouchpointApi,
  firstName: string,
): ActivityEvent | undefined {
  const occurredAt = parseDateParam(touchpoint.occurredAt);
  if (!occurredAt) return undefined;

  const what = (() => {
    switch (touchpoint.toStatus) {
      case 'New':
        return 'Sourced';
      case 'Shortlisted':
        return 'Shortlisted';
      case 'ContactStarted':
        return 'Outreach started on LinkedIn';
      case 'Contacted':
        return 'Outreach sent on LinkedIn';
      case 'Declined':
        return `${firstName} declined`;
      case 'PrescreeningScheduled':
        return 'Pre-screen call scheduled';
      case 'Prescreened':
      case 'AiPrescreened':
        return 'Pre-screen call completed';
      case 'AssessmentCompleted':
        return 'Assessment completed';
      case 'ReadyForSubmission':
        return 'Introduction prepared';
      case 'Submitted':
        return 'Introduced to you';
      case 'HiringManagerApproved':
        return 'Approved for a first-round interview';
      case 'Interviewing':
        return 'Interview requested';
      case 'Rejected':
        return touchpoint.fromStatus === 'Submitted' ||
          touchpoint.fromStatus === 'HiringManagerApproved' ||
          touchpoint.fromStatus === 'Interviewing'
          ? 'Passed'
          : 'Conversation closed';
      case 'Hired':
        return 'Hired';
      default:
        return humanize(touchpoint.kind);
    }
  })();

  return { what, occurredAt, dayKey: format(occurredAt, 'yyyy-MM-dd') };
}

const dayLabel = (date: Date) =>
  isSameYear(date, new Date())
    ? format(date, 'MMM d')
    : format(date, 'MMM d, yyyy');

/** Compact timeline grouped by day, newest first. */
export function CandidateActivity({
  firstName,
  params,
}: CandidateActivityProps) {
  const touchpointsQuery = useCandidateTouchpointsSuspenseQuery(params);
  const events = touchpointsQuery.data
    .map((touchpoint) => toEvent(touchpoint, firstName))
    .filter((event): event is ActivityEvent => event !== undefined)
    .sort((a, b) => b.occurredAt.getTime() - a.occurredAt.getTime());

  if (events.length === 0) {
    return (
      <p className="text-muted-foreground border-y py-4 text-[13px]">
        Candidate progress will appear here as it is recorded.
      </p>
    );
  }

  const dayKeys = [...new Set(events.map((event) => event.dayKey))];

  return (
    <div className="flex flex-col gap-2">
      {dayKeys.map((dayKey, dayIndex) => {
        const list = events.filter((event) => event.dayKey === dayKey);
        const isLastDay = dayIndex === dayKeys.length - 1;
        return (
          <section className="flex flex-col" key={dayKey}>
            <h3 className="mb-3 grid grid-cols-[48px_16px_minmax(0,1fr)] gap-x-4 text-[13px] font-semibold">
              <span className="col-start-3">
                {dayLabel(list[0]?.occurredAt ?? new Date())}
              </span>
            </h3>
            <ol className="flex flex-col">
              {list.map((event, index) => {
                const isLast = isLastDay && index === list.length - 1;
                return (
                  <li
                    className="grid grid-cols-[48px_16px_minmax(0,1fr)] gap-x-4"
                    key={`${event.what}-${event.occurredAt.toISOString()}`}
                  >
                    <time
                      className="pt-0.5 text-right text-xs text-neutral-500 tabular-nums"
                      dateTime={event.occurredAt.toISOString()}
                    >
                      {format(event.occurredAt, 'HH:mm')}
                    </time>
                    <span className="relative flex flex-col items-center">
                      <span
                        className={cn(
                          'border-input bg-background z-[1] mt-1.5 size-2 shrink-0 rounded-full border-2',
                          index === 0 && dayIndex === 0 && 'border-foreground',
                        )}
                      />
                      {!isLast && <span className="bg-border w-px flex-1" />}
                    </span>
                    <p className="pb-5">{event.what}</p>
                  </li>
                );
              })}
            </ol>
          </section>
        );
      })}
    </div>
  );
}
